import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { IAdminTransaction } from '@/types/admin/transactions';
import { 
  getPaymentStatusColor, 
  isPayment as checkIsPayment,
  getTransactionAmountColor,
  getTransactionTypeLabel,
  hasTransactionDetails 
} from '@/utils/admin/transactions';

interface TransactionListProps {
  transactions: IAdminTransaction[];
  maxRows?: number; // If undefined, shows first 5; if 0 or null, shows all
}

export const TransactionList: React.FC<TransactionListProps> = ({ 
  transactions, 
  maxRows,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());

  // If maxRows is undefined, default to 5; if 0 or null, show all
  const displayTransactions = maxRows === undefined 
    ? transactions.slice(0, 5)
    : maxRows > 0 
    ? transactions.slice(0, maxRows) 
    : transactions;

  const toggleExpand = (id: string | number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const renderTransaction = ({ item }: { item: IAdminTransaction }) => {
    const isExpanded = expandedIds.has(item.transactionId);
    const isPayment = checkIsPayment(item);
    const hasDetails = hasTransactionDetails(item);

    return (
      <TouchableOpacity 
        style={styles.card}
        onPress={hasDetails ? () => toggleExpand(item.transactionId) : undefined}
        activeOpacity={hasDetails ? 0.7 : 1}
        disabled={!hasDetails}
      >
        {/* Header */}
        <View style={styles.cardHeader}>
          <View style={styles.headerLeft}>
            <Text style={styles.date}>{formatShortDate(item.transactionDate)}</Text>
            <View style={[styles.typeBadge, isPayment ? styles.paymentBadge : styles.chargeBadge]}>
              <Text style={styles.typeBadgeText}>
                {getTransactionTypeLabel(item)}
              </Text>
            </View>
          </View>
          <Text style={[
            styles.amount,
            { color: getTransactionAmountColor(item), fontWeight: '700' }
          ]}>
            {formatCurrency(Math.abs(item.amount))}
          </Text>
        </View>

        {/* Description */}
        <Text style={styles.description} numberOfLines={isExpanded ? undefined : 2}>
          {item.description}
        </Text>

        {item.arCodeDescription && (
          <Text style={styles.arCode}>{item.arCodeDescription}</Text>
        )}

        {/* Expanded Details */}
        {isExpanded && hasDetails && (
          <View style={styles.expandedSection}>
            {item.paidByFirstName && item.paidByLastName && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Paid By:</Text>
                <Text style={styles.detailValue}>
                  {item.paidByFirstName} {item.paidByLastName}
                </Text>
              </View>
            )}

            {item.paymentSettlementStatus && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Status:</Text>
                <View style={[
                  styles.statusBadge,
                  { backgroundColor: getPaymentStatusColor(item.paymentSettlementStatus) + '20' }
                ]}>
                  <Text style={[
                    styles.statusText,
                    { color: getPaymentStatusColor(item.paymentSettlementStatus) }
                  ]}>
                    {item.paymentSettlementStatus}
                  </Text>
                </View>
              </View>
            )}

            {item.lateFees?.amount && item.lateFees.amount > 0 && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Late Fee:</Text>
                <Text style={[styles.detailValue, styles.feeValue]}>
                  {formatCurrency(item.lateFees.amount)}
                </Text>
              </View>
            )}

            {item.paymentConvenienceFee && item.paymentConvenienceFee > 0 && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Convenience Fee:</Text>
                <Text style={styles.detailValue}>
                  {formatCurrency(item.paymentConvenienceFee)}
                </Text>
              </View>
            )}

            {item.postMonth && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Post Month:</Text>
                <Text style={styles.detailValue}>
                  {formatShortDate(item.postMonth)}
                </Text>
              </View>
            )}

            {item.arCode && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>AR Code:</Text>
                <Text style={styles.detailValue}>
                  {item.arCode}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Expand/Collapse Indicator - only show if there are details */}
        {hasDetails && (
          <View style={styles.expandIndicator}>
            <Text style={styles.expandIndicatorText}>
              {isExpanded ? 'Tap to collapse' : 'Tap for details'}
            </Text>
            {isExpanded ? (
              <SVG_COMPONENTS.CHEVRON_UP width={12} height={12} color={ADMIN_COLORS.TEXT_TERTIARY} />
            ) : (
              <SVG_COMPONENTS.CHEVRON_RIGHT width={12} height={12} color={ADMIN_COLORS.TEXT_TERTIARY} />
            )}
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <FlatList
      data={displayTransactions}
      renderItem={renderTransaction}
      keyExtractor={(item) => String(item.transactionId)}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      showsVerticalScrollIndicator={false}
      style={styles.list}
    />
  );
};

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  card: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    padding: 16,
    borderRadius: 8,
    marginVertical: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  date: {
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  paymentBadge: {
    backgroundColor: ADMIN_COLORS.TRANSACTION.PAYMENT + '20',
  },
  chargeBadge: {
    backgroundColor: ADMIN_COLORS.TRANSACTION.CHARGE + '20',
  },
  typeBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  amount: {
    fontSize: 18,
    // Color applied dynamically via getTransactionAmountColor
  },
  description: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  arCode: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 8,
  },
  expandedSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },
  detailLabel: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '500',
  },
  detailValue: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '600',
  },
  feeValue: {
    color: ADMIN_COLORS.TRANSACTION.CHARGE,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  expandIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    gap: 4,
  },
  expandIndicatorText: {
    fontSize: 11,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  separator: {
    height: 8,
  },
});

